import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN, stickerButton } from "../../theme";
import { WRITING_CAST_ASSET, WRITING_WORD_ASSET } from "../writingAssets";

/*
 * Shared Writing Town UI. Every export here is used by several engines
 * (including Sound / Sentence / Story engines owned elsewhere), so props are
 * backward compatible: PromptCard, ChoiceButton, WordPicture, CastPicture and
 * GameFrame keep their original signatures and only gained optional props.
 */

// ── colour helpers ────────────────────────────────────────────────────────
export function withAlpha(hex, alpha = 0.12) {
  const raw = String(hex || LEARN.color.lavender).replace("#", "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function darken(hex, amount = 0.32) {
  const raw = String(hex || LEARN.color.lavender).replace("#", "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  const n = parseInt(full, 16) || 0;
  const ch = (v) => Math.max(0, Math.round(v * (1 - amount))).toString(16).padStart(2, "0");
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}

// ── paper & wood ──────────────────────────────────────────────────────────
/**
 * Writing Town sits on a desk: panels are paper, pieces are wooden tiles.
 * PAPER_GRAIN is a tiny inline SVG noise tile (no hosted asset, no request)
 * laid over the warm paper tint at ~4% so the surface reads as fibre, not flat.
 */
const GRAIN_SVG = `<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='g'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.04  0 0 0 0 0.10  0 0 0 0 0.20  0 0 0 0.045 0'/></filter><rect width='100%' height='100%' filter='url(#g)'/></svg>`;
export const PAPER = {
  bg: "#FFFDF7",
  grain: `url("data:image/svg+xml,${encodeURIComponent(GRAIN_SVG)}")`,
  radius: 24,
  /** soft inner shade along the top edge, like the sheet curls off the desk */
  innerShade: "inset 0 14px 18px -14px rgba(11,26,51,0.16)",
};

/** Style object for a sheet of paper (pads, boards, storybooks). */
export function paperSurface({ radius = PAPER.radius, border = LEARN.color.line, frosted = false } = {}) {
  return {
    backgroundColor: frosted ? "rgba(255,253,247,0.94)" : PAPER.bg,
    backgroundImage: PAPER.grain,
    backdropFilter: frosted ? "blur(8px)" : undefined,
    WebkitBackdropFilter: frosted ? "blur(8px)" : undefined,
    borderRadius: radius,
    border: `2px solid ${border}`,
    boxShadow: `${LEARN.shadow.card}, ${PAPER.innerShade}`,
  };
}

/** Resting / pressed shadows for a wooden tile with a coloured bottom edge. */
export function tileEdge(edgeColor, depth = 4) {
  return {
    rest: `0 ${depth}px 0 ${edgeColor}, 0 10px 20px rgba(11,26,51,0.08)`,
    pressed: `0 1px 0 ${edgeColor}, 0 4px 10px rgba(11,26,51,0.06)`,
  };
}

/** A white sticker frame for hosted cutouts: no coloured edge, a soft lifted shadow. */
export const PICTURE_FRAME = {
  rest: "0 2px 0 rgba(11,26,51,0.05), 0 12px 24px rgba(11,26,51,0.12)",
  pressed: "0 1px 0 rgba(11,26,51,0.05), 0 5px 12px rgba(11,26,51,0.08)",
};

/** Letter glyphs on tiles stay in the 26–34px band; words step down so they never clip. */
export const TILE_GLYPH_SIZE = "clamp(26px, calc(var(--tile, 72px) * 0.4), 34px)";
export const TILE_WORD_SIZE = "clamp(18px, calc(var(--tile, 72px) * 0.26), 26px)";

/** The one in-arena pill style: small, frosted, hairline, no heavy shadow. */
export function frostedPill({ accent, tone = "neutral" } = {}) {
  const warm = tone === "urgent";
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: LEARN.radius.pill,
    background: warm ? withAlpha(LEARN.color.coral, 0.12) : "rgba(255,255,255,0.72)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    border: `1.5px solid ${warm ? LEARN.color.coral : accent ? withAlpha(accent, 0.28) : "rgba(11,26,51,0.10)"}`,
    boxShadow: "0 2px 6px rgba(11,26,51,0.06)",
    fontFamily: LEARN.font,
    fontWeight: 700,
    fontSize: 14,
    color: warm ? LEARN.color.coral : LEARN.color.ink,
    flexShrink: 0,
    whiteSpace: "nowrap",
    lineHeight: 1.2,
  };
}

// ── hooks ─────────────────────────────────────────────────────────────────
/** Fire onComplete at most once for this mount. */
export function useCompleteOnce(onComplete) {
  const done = useRef(false);
  return useCallback(() => {
    if (done.current) return;
    done.current = true;
    onComplete?.();
  }, [onComplete]);
}

/** Seconds countdown; `onEnd` fires exactly once when it hits zero. */
export function useCountdown(total, { running = true, onEnd } = {}) {
  const [seconds, setSeconds] = useState(total);
  const endRef = useRef(onEnd);
  endRef.current = onEnd;
  const firedRef = useRef(false);
  useEffect(() => {
    if (!running || seconds <= 0) return undefined;
    const timer = setTimeout(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [running, seconds]);
  useEffect(() => {
    if (running && seconds <= 0 && !firedRef.current) {
      firedRef.current = true;
      endRef.current?.();
    }
  }, [running, seconds]);
  return seconds;
}

/**
 * Speak the round's item (a letter, a clue) — never the activity prompt,
 * which the player speaks ~350ms after mount with `interrupt`. Waiting a
 * beat queues the item line behind the intro instead of being cancelled by it.
 */
export function useSpokenItem(speak, text, { delay = 700, enabled = true } = {}) {
  useEffect(() => {
    if (!enabled || !text || !speak) return undefined;
    const timer = setTimeout(() => speak(text, { dedupe: true }), delay);
    return () => clearTimeout(timer);
  }, [delay, enabled, speak, text]);
}

/** Flash an id as "wrong" for a moment (drives ShakeWrap / ChoiceButton wrong). */
export function useWrongFlash(ms = 460) {
  const [wrongId, setWrongId] = useState(null);
  const timer = useRef(null);
  const flash = useCallback((id = "global") => {
    setWrongId(id);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setWrongId(null), ms);
  }, [ms]);
  useEffect(() => () => timer.current && clearTimeout(timer.current), []);
  return { wrongId, flash, isWrong: (id = "global") => wrongId === id };
}

// ── layout ────────────────────────────────────────────────────────────────
/**
 * Engine root: fills the LearnStage arena, never lets content escape it.
 * `center` stacks the content in the middle of the arena using auto margins,
 * which fall back to top-aligned + scrolling when the content is taller.
 */
export const GameFrame = ({ children, gap = 12, style, center = false }) => (
  <div
    style={{
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      position: "relative",
      display: "flex",
      flexDirection: "column",
      gap,
      overflow: center ? "auto" : "hidden",
      WebkitOverflowScrolling: "touch",
      fontFamily: LEARN.font,
      color: LEARN.color.ink,
      ...style,
    }}
  >
    {center ? (
      <div style={{ margin: "auto 0", minHeight: 0, display: "flex", flexDirection: "column", gap }}>{children}</div>
    ) : (
      children
    )}
  </div>
);

/** Scrollable middle section for engines whose content can exceed the arena. */
export const ScrollArea = ({ children, style }) => (
  <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch", ...style }}>
    {children}
  </div>
);

/** Wrapping row of sticker tiles, centred. */
export const ChoiceRow = ({ children, gap = 12, style, wrap = true }) => (
  <div
    style={{
      display: "flex",
      flexWrap: wrap ? "wrap" : "nowrap",
      justifyContent: "center",
      alignItems: "center",
      gap,
      flexShrink: 0,
      ...style,
    }}
  >
    {children}
  </div>
);

/** Gentle horizontal shake for a wrong attempt (kept instant under reduced motion). */
export const ShakeWrap = ({ shake, reduceMotion = false, children, style, ...rest }) => (
  <motion.div
    animate={shake && !reduceMotion ? { x: [0, -8, 8, -6, 6, -3, 3, 0] } : { x: 0 }}
    transition={{ duration: 0.45, ease: "easeInOut" }}
    style={{ display: "contents", ...style }}
    {...rest}
  >
    {children}
  </motion.div>
);

// ── icons (inline SVG only — no emoji, no text-as-icon) ───────────────────
const iconProps = (size) => ({
  width: size, height: size, viewBox: "0 0 24 24", fill: "none",
  stroke: "currentColor", strokeWidth: 2.6, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true,
});
export const UndoIcon = ({ size = 22 }) => (
  <svg {...iconProps(size)}><path d="M9 14L4 9l5-5" /><path d="M4 9h9.5a6.5 6.5 0 010 13H11" /></svg>
);
export const ClearIcon = ({ size = 22 }) => (
  <svg {...iconProps(size)}><path d="M4 7h16" /><path d="M9 7V4h6v3" /><path d="M6 7l1 13h10l1-13" /><path d="M10 11v6M14 11v6" /></svg>
);
export const CheckIcon = ({ size = 22, strokeWidth = 3 }) => (
  <svg {...iconProps(size)} strokeWidth={strokeWidth}><path d="M4.5 12.5l4.8 4.8L19.5 7" /></svg>
);
export const SpeakerIcon = ({ size = 22 }) => (
  <svg {...iconProps(size)}><path d="M4 10v4h3l4 4V6L7 10H4z" fill="currentColor" /><path d="M15 9.5a3.5 3.5 0 010 5" /><path d="M17.5 7a7 7 0 010 10" /></svg>
);
export const EyeIcon = ({ size = 22, off = false }) => (
  <svg {...iconProps(size)}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="3" />
    {off && <path d="M4 20L20 4" />}
  </svg>
);
export const PlayIcon = ({ size = 22 }) => (
  <svg {...iconProps(size)}><path d="M7 5v14l12-7z" fill="currentColor" /></svg>
);
export const ArrowIcon = ({ size = 22 }) => (
  <svg {...iconProps(size)}><path d="M5 12h14" /><path d="M13 6l6 6-6 6" /></svg>
);

/** The catalog's activity `symbol` slug (trace, words, grid, listen…) as an inline icon. */
const SYMBOL_PATHS = {
  trace: <><path d="M4 20l4.2-1 9.6-9.6a2.1 2.1 0 000-3l-.2-.2a2.1 2.1 0 00-3 0L5 15.8 4 20z" /><path d="M13 7.5l3.5 3.5" /></>,
  words: <><path d="M4 7h16" /><path d="M4 12h11" /><path d="M4 17h14" /></>,
  grid: <><rect x="4" y="4" width="6.5" height="6.5" rx="1.6" /><rect x="13.5" y="4" width="6.5" height="6.5" rx="1.6" /><rect x="4" y="13.5" width="6.5" height="6.5" rx="1.6" /><rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.6" /></>,
  listen: <><path d="M4 10v4h3l4 4V6L7 10H4z" fill="currentColor" /><path d="M15 9.5a3.5 3.5 0 010 5" /><path d="M17.5 7a7 7 0 010 10" /></>,
  collect: <><path d="M4 9h16l-1.6 10.2a1.5 1.5 0 01-1.5 1.3H7.1a1.5 1.5 0 01-1.5-1.3L4 9z" /><path d="M8.5 9V7a3.5 3.5 0 017 0v2" /></>,
  touch: <><path d="M9.5 12.5V6a2 2 0 014 0v5" /><path d="M13.5 10.5a2 2 0 014 0v2.5" /><path d="M9.5 12.5L7.6 10.9a2 2 0 00-2.8 2.7l3.9 5.4A5 5 0 0012.8 21h1.4a4.5 4.5 0 004.5-4.5V13" /></>,
  sort: <><path d="M7 4v16" /><path d="M4 17l3 3 3-3" /><path d="M17 20V4" /><path d="M14 7l3-3 3 3" /></>,
  search: <><circle cx="10.5" cy="10.5" r="6" /><path d="M15 15l5 5" /></>,
  check: <path d="M4.5 12.5l4.8 4.8L19.5 7" />,
  star: <path d="M12 3l2.5 5.6 6 .7-4.5 4 1.3 6-5.3-3.1L6.7 19.3l1.3-6-4.5-4 6-.7z" />,
};
export const SymbolIcon = ({ name, size = 18 }) => (
  <svg {...iconProps(size)} strokeWidth={2.4}>{SYMBOL_PATHS[name] || SYMBOL_PATHS.star}</svg>
);

// ── buttons ───────────────────────────────────────────────────────────────
/**
 * Round icon button (undo / clear / hear). Real button, focus ring.
 * `frosted` is the quiet tool style that sits on paper (pads): translucent
 * white, blur, hairline border, no drop shadow.
 */
export const IconButton = ({ label, onClick, children, accent = LEARN.color.ink, disabled = false, size = 46, filled = false, frosted = false, style }) => (
  <motion.button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    disabled={disabled}
    whileTap={disabled ? undefined : { scale: 0.9 }}
    whileHover={disabled ? undefined : { scale: 1.06 }}
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      border: `${frosted ? 1.5 : 2}px solid ${filled ? "transparent" : frosted ? "rgba(11,26,51,0.10)" : LEARN.color.line}`,
      background: filled ? accent : frosted ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.96)",
      backdropFilter: frosted ? "blur(8px)" : undefined,
      WebkitBackdropFilter: frosted ? "blur(8px)" : undefined,
      color: filled ? "#fff" : accent,
      boxShadow: filled ? LEARN.shadow.sticker(darken(accent)) : frosted ? "0 2px 6px rgba(11,26,51,0.06)" : LEARN.shadow.card,
      display: "grid",
      placeItems: "center",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.38 : 1,
      padding: 0,
      flexShrink: 0,
      WebkitTapHighlightColor: "transparent",
      transition: "opacity 160ms, background 160ms",
      ...style,
    }}
  >
    {children}
  </motion.button>
);

/** Chunky sticker action button (Check / Done). Always at least 56px tall at `lg`. */
export const StickerAction = ({ children, onClick, accent = LEARN.color.lavender, disabled = false, size = "md", icon, style, ariaLabel }) => (
  <motion.button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    disabled={disabled}
    whileTap={disabled ? undefined : { scale: 0.96, y: 3 }}
    whileHover={disabled ? undefined : { y: -2 }}
    style={{
      ...stickerButton({ bg: disabled ? "rgba(11,26,51,0.22)" : accent, deep: disabled ? "rgba(11,26,51,0.12)" : darken(accent), size }),
      minHeight: size === "lg" ? 58 : size === "sm" ? 42 : 50,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      cursor: disabled ? "not-allowed" : "pointer",
      ...style,
    }}
  >
    {icon}
    {children}
  </motion.button>
);

/** Slim accent strip naming the activity (symbol + title) across the top of a board. */
export const PanelHeader = ({ symbol, title, accent = LEARN.color.lavender, radius = PAPER.radius, right }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 14px",
      background: withAlpha(accent, 0.12),
      borderBottom: `1.5px solid ${withAlpha(accent, 0.22)}`,
      borderRadius: `${radius - 2}px ${radius - 2}px 0 0`,
      flexShrink: 0,
      minHeight: 42,
    }}
  >
    {symbol && (
      <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: "50%", background: accent, color: "#fff", display: "grid", placeItems: "center", boxShadow: LEARN.shadow.sticker(darken(accent)), flexShrink: 0 }}>
        <SymbolIcon name={symbol} size={16} />
      </span>
    )}
    {title && <span style={{ fontFamily: LEARN.font, fontWeight: 700, fontSize: 15, color: darken(accent, 0.25), letterSpacing: 0.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{title}</span>}
    {right && <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8 }}>{right}</span>}
  </div>
);

/**
 * The working surface for a round: a sheet of paper with an accent header
 * strip, sized to its content plus generous padding (never stretched to fill
 * the arena), centred pieces inside. Pair with a centred frame/arena.
 */
export const Board = ({ children, title, symbol, caption, accent = LEARN.color.lavender, right, style, bodyStyle, minHeight = "clamp(170px, calc(var(--arena-h, 600px) * 0.34), 340px)", pad = "calc(var(--tile, 72px) * 0.3) calc(var(--tile, 72px) * 0.28)" }) => (
  <div style={{ ...paperSurface({ frosted: true }), display: "flex", flexDirection: "column", flexShrink: 0, minWidth: 0, overflow: "hidden", fontFamily: LEARN.font, color: LEARN.color.ink, ...style }}>
    {(title || symbol || right) && <PanelHeader symbol={symbol} title={title} accent={accent} right={right} />}
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18, minHeight, padding: pad, ...bodyStyle }}>
      {caption && <div style={{ fontSize: 16, fontWeight: 600, color: LEARN.color.muted, textAlign: "center", lineHeight: 1.3 }}>{caption}</div>}
      {children}
    </div>
  </div>
);

// ── cards & tiles ─────────────────────────────────────────────────────────
/**
 * In-arena card for round content (a sentence frame, a clue). The activity
 * instruction itself lives in the stage prompt, so keep this for content only.
 */
export const PromptCard = ({ text, accent = LEARN.color.lavender, actionTitle, onAction, children, size = "md", style }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: size === "lg" ? "14px 18px" : "10px 16px",
      minHeight: 56,
      borderRadius: LEARN.radius.md,
      background: "rgba(255,255,255,0.94)",
      border: `2px solid ${LEARN.color.line}`,
      boxShadow: LEARN.shadow.card,
      flexShrink: 0,
      fontFamily: LEARN.font,
      ...style,
    }}
  >
    <div
      style={{
        flex: 1,
        minWidth: 0,
        fontWeight: 700,
        fontSize: size === "lg" ? 24 : 20,
        lineHeight: 1.3,
        color: LEARN.color.ink,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
      }}
    >
      {text}
      {children}
    </div>
    {actionTitle && (
      <StickerAction onClick={onAction} accent={accent} size="sm">{actionTitle}</StickerAction>
    )}
  </div>
);

/**
 * A wooden sticker tile sized by the stage's --tile custom property: white
 * face, hairline border, a 4px bottom edge in the accent-deep colour, lifts
 * on hover and squashes onto its edge when pressed. `variant="picture"` is
 * the white frame for hosted cutouts (soft shadow, no coloured edge).
 */
export const StickerTile = ({
  children,
  size,                 // number (px) or CSS length; default var(--tile)
  accent = LEARN.color.lavender,
  selected = false,
  wrong = false,
  correct = false,
  dim = false,
  onClick,
  disabled = false,
  ariaLabel,
  ariaPressed,
  reduceMotion = false,
  variant = "tile",     // tile | picture
  style,
  as = "button",
  ...rest
}) => {
  const dimension = size == null ? "var(--tile, 72px)" : typeof size === "number" ? `${size}px` : size;
  const picture = variant === "picture";
  const deep = darken(accent);
  const border = wrong ? LEARN.color.coral : correct ? LEARN.color.mint : selected ? deep : picture ? "rgba(11,26,51,0.10)" : LEARN.color.line;
  const edgeColor = wrong ? LEARN.color.coralDeep : correct ? LEARN.color.mintDeep : selected ? darken(accent, 0.5) : deep;
  const edge = picture && !selected && !correct && !wrong ? PICTURE_FRAME : tileEdge(edgeColor, 4);
  const interactive = !!onClick && !disabled;
  const Comp = onClick || as === "button" ? motion.button : motion.div;
  return (
    <Comp
      type={Comp === motion.button ? "button" : undefined}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      onClick={onClick}
      disabled={disabled}
      whileTap={interactive ? { scale: 0.97, y: 3, boxShadow: edge.pressed } : undefined}
      whileHover={interactive && !reduceMotion ? { y: -3 } : undefined}
      animate={
        wrong && !reduceMotion
          ? { x: [0, -7, 7, -5, 5, -2, 0] }
          : correct && !reduceMotion
            ? { scale: [1, 1.12, 1] }
            : { x: 0, scale: 1 }
      }
      transition={{ duration: 0.42 }}
      style={{
        width: dimension,
        height: dimension,
        minWidth: 56,
        minHeight: 56,
        flexShrink: 0,
        borderRadius: picture ? LEARN.radius.md : "22%",
        background: selected ? accent : correct ? withAlpha(LEARN.color.mint, 0.16) : "#fff",
        color: selected ? "#fff" : LEARN.color.ink,
        border: `2px solid ${border}`,
        boxShadow: edge.rest,
        display: "grid",
        placeItems: "center",
        padding: picture ? "5%" : 0,
        cursor: interactive ? "pointer" : "default",
        opacity: dim ? 0.45 : 1,
        fontFamily: LEARN.font,
        fontWeight: 700,
        WebkitTapHighlightColor: "transparent",
        transition: reduceMotion ? "none" : "background 180ms, border-color 180ms, opacity 200ms, box-shadow 160ms",
        ...style,
      }}
      {...rest}
    >
      {children}
    </Comp>
  );
};

/** Letter / word choice as a sticker tile. `flipped` mirrors the glyph (Which Is Right?). */
export const ChoiceButton = ({
  label,
  accent = LEARN.color.lavender,
  selected,
  onClick,
  flipped,
  image,
  wrong = false,
  correct = false,
  disabled = false,
  dim = false,
  size,
  ariaLabel,
  reduceMotion = false,
  fontSize,
  style,
}) => (
  <StickerTile
    accent={accent}
    selected={selected}
    wrong={wrong}
    correct={correct}
    dim={dim}
    onClick={onClick}
    disabled={disabled}
    size={size}
    ariaLabel={ariaLabel || (typeof label === "string" ? label : undefined)}
    ariaPressed={selected != null ? !!selected : undefined}
    reduceMotion={reduceMotion}
    style={style}
  >
    {image ? (
      <img src={image} alt="" draggable={false} style={{ width: "78%", height: "78%", objectFit: "contain", pointerEvents: "none" }} />
    ) : (
      <span
        style={{
          // Fredoka glyphs scale with the tile but stay in the 26–48px band
          // (long words step down so they never clip the tile).
          fontSize: fontSize || (String(label).length > 2 ? TILE_WORD_SIZE : TILE_GLYPH_SIZE),
          lineHeight: 1,
          transform: flipped ? "scaleX(-1)" : "none",
          display: "inline-block",
          padding: "0 6px",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    )}
  </StickerTile>
);

/** Hosted word cutout on a sticker tile (numeric `size` keeps old callers working). */
export const WordPicture = ({ name, alt, size, plain = false, onClick, accent, selected, wrong, correct, dim, ariaLabel, reduceMotion, style }) => {
  const [broken, setBroken] = useState(false);
  if (!name || broken) return null;
  const img = (
    <img
      src={WRITING_WORD_ASSET(name)}
      alt={alt || name}
      draggable={false}
      onError={() => setBroken(true)}
      style={plain ? { width: size || "100%", height: size || "100%", objectFit: "contain", pointerEvents: "none" } : { width: "80%", height: "80%", objectFit: "contain", pointerEvents: "none" }}
    />
  );
  if (plain) return img;
  return (
    <StickerTile
      as="div"
      variant="picture"
      size={size == null ? "calc(var(--tile, 72px) * 1.15)" : size}
      accent={accent}
      onClick={onClick}
      selected={selected}
      wrong={wrong}
      correct={correct}
      dim={dim}
      ariaLabel={ariaLabel || (onClick ? alt || name : undefined)}
      reduceMotion={reduceMotion}
      style={style}
    >
      {img}
    </StickerTile>
  );
};

/** Pixley cast member (Zippy / Pip / Benny) on a sticker tile. */
export const CastPicture = ({ name, alt, size, plain = false, onClick, accent, selected, wrong, correct, ariaLabel, reduceMotion, style }) => {
  const [broken, setBroken] = useState(false);
  const img = broken ? (
    <span style={{ fontSize: 28, fontWeight: 700, color: LEARN.color.muted }}>{(alt || name || "?")[0]}</span>
  ) : (
    <img
      src={WRITING_CAST_ASSET(name)}
      alt={alt || name}
      draggable={false}
      onError={() => setBroken(true)}
      style={plain ? { width: size || "100%", height: size || "100%", objectFit: "contain", pointerEvents: "none" } : { width: "82%", height: "82%", objectFit: "contain", pointerEvents: "none" }}
    />
  );
  if (plain) return img;
  return (
    <StickerTile
      as="div"
      variant="picture"
      size={size == null ? "calc(var(--tile, 72px) * 1.3)" : size}
      accent={accent}
      onClick={onClick}
      selected={selected}
      wrong={wrong}
      correct={correct}
      ariaLabel={ariaLabel || (onClick ? alt || name : undefined)}
      reduceMotion={reduceMotion}
      style={style}
    >
      {img}
    </StickerTile>
  );
};

// ── round chrome ──────────────────────────────────────────────────────────
/**
 * The one round counter: a small frosted pill, "3 of 9", with progress dots
 * when they fit. `current` is 1-based; `done` is how many rounds are finished
 * (the dots). Sits top-right of the working area.
 */
export const CounterPill = ({ current, total, done = Math.max(0, current - 1), accent = LEARN.color.lavender, label, style }) => (
  <div aria-label={`${label ? `${label} ` : ""}${current} of ${total}`} style={{ ...frostedPill(), ...style }}>
    {label && <span style={{ color: LEARN.color.muted }}>{label}</span>}
    <span style={{ fontVariantNumeric: "tabular-nums" }}>{current} of {total}</span>
    {total <= 10 && (
      <span aria-hidden="true" style={{ display: "inline-flex", gap: 4 }}>
        {Array.from({ length: total }, (_, i) => (
          <span key={i} style={{ width: 7, height: 7, borderRadius: 999, background: i < done ? accent : "rgba(11,26,51,0.14)", transition: "background 200ms" }} />
        ))}
      </span>
    )}
  </div>
);

/** "3 of 9" where `done` rounds are finished (shows done + 1). */
export const RoundCounter = ({ done, total, accent, label, style }) => (
  <CounterPill current={Math.min(done + 1, total)} done={done} total={total} accent={accent} label={label} style={style} />
);

/** Small dots for repeats within a round (trace 1 of 2). */
export const RepeatDots = ({ total, done, accent = LEARN.color.lavender }) => (
  <span aria-label={`${done} of ${total} done`} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
    {Array.from({ length: total }, (_, i) => (
      <span
        key={i}
        style={{
          width: 12,
          height: 12,
          borderRadius: 999,
          background: i < done ? accent : "#fff",
          border: `2px solid ${i < done ? accent : "rgba(11,26,51,0.22)"}`,
          transition: "background 200ms",
        }}
      />
    ))}
  </span>
);

/** The glyph the child is working on, with a caption ("Trace", "Write"). `hidden` shows a question mark. */
export const TargetChip = ({ glyph, caption, accent = LEARN.color.lavender, hidden = false, size, style, children }) => {
  const long = !hidden && String(glyph).length > 2;
  const tile = size || "calc(var(--tile, 72px) * 0.95)";
  return (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 10, flexShrink: 0, minWidth: 0, ...style }}>
    <StickerTile as="div" size={tile} accent={accent} style={{ borderColor: withAlpha(accent, 0.35), ...(long ? { width: "auto", minWidth: tile, padding: "0 14px" } : null) }}>
      <span style={{ fontSize: hidden ? "calc(var(--tile, 72px) * 0.42)" : long ? "clamp(20px, calc(var(--tile, 72px) * 0.36), 40px)" : "calc(var(--tile, 72px) * 0.5)", lineHeight: 1, color: hidden ? LEARN.color.muted : accent, whiteSpace: "nowrap" }}>
        {hidden ? "?" : glyph}
      </span>
    </StickerTile>
    {(caption || children) && (
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        {caption && <span style={{ fontWeight: 700, fontSize: 17, color: LEARN.color.ink, whiteSpace: "nowrap" }}>{caption}</span>}
        {children}
      </div>
    )}
  </div>
  );
};

/** Countdown pill for the "friendly minute" games. */
export const TimerPill = ({ seconds, style }) => {
  const urgent = seconds <= 10;
  return (
    <div
      aria-live="polite"
      aria-label={`${seconds} seconds left`}
      style={{
        ...frostedPill({ tone: urgent ? "urgent" : "neutral" }),
        fontSize: 16,
        fontVariantNumeric: "tabular-nums",
        ...style,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2" /><path d="M9 3h6" />
      </svg>
      {seconds}s
    </div>
  );
};

/** Score pill: "5 letters" with an optional personal best. */
export const ScorePill = ({ score, unit = "letters", best, accent = LEARN.color.lavender, style }) => (
  <div aria-live="polite" style={{ ...frostedPill({ accent }), ...style }}>
    <span style={{ fontVariantNumeric: "tabular-nums", color: accent, fontSize: 18 }}>{score}</span>
    <span style={{ fontWeight: 600 }}>{unit}</span>
    {best > 0 && <span style={{ color: LEARN.color.muted, fontWeight: 600, fontSize: 13 }}>best {best}</span>}
  </div>
);

/** A row of what has been collected this round (letters written, names tagged). */
export const CollectedStrip = ({ items, accent = LEARN.color.lavender, empty, reduceMotion = false, style }) => (
  <div
    style={{
      display: "flex",
      gap: 8,
      alignItems: "center",
      overflowX: "auto",
      overflowY: "hidden",
      minHeight: "calc(var(--tile, 72px) * 0.42 + 10px)",
      padding: "4px 2px 6px",
      flexShrink: 0,
      scrollbarWidth: "none",
      ...style,
    }}
  >
    {!items.length && empty && <span style={{ fontSize: 14, color: LEARN.color.muted, fontWeight: 600 }}>{empty}</span>}
    {items.map((item, i) => (
      <motion.span
        key={`${item}-${i}`}
        initial={reduceMotion ? false : { scale: 0.4, opacity: 0, rotate: -12 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 420, damping: 18 }}
        style={{
          minWidth: "calc(var(--tile, 72px) * 0.42)",
          height: "calc(var(--tile, 72px) * 0.42)",
          padding: "0 10px",
          borderRadius: 12,
          background: "#fff",
          border: `2px solid ${LEARN.color.line}`,
          boxShadow: tileEdge(darken(accent), 3).rest,
          display: "grid",
          placeItems: "center",
          fontFamily: LEARN.font,
          fontWeight: 700,
          fontSize: "clamp(18px, calc(var(--tile, 72px) * 0.22), 24px)",
          color: accent,
          flexShrink: 0,
        }}
      >
        {item}
      </motion.span>
    ))}
  </div>
);

/** A sentence with a blank the child writes into: "The ____ sat." */
export const SentenceFrame = ({ before = "", after = "", filled, accent = LEARN.color.lavender, blankWidth, size = "md", style, children }) => {
  const blank = filled ? (
    <span style={{ color: accent, borderBottom: `3px solid ${accent}`, padding: "0 6px" }}>{filled}</span>
  ) : (
    <span
      aria-label="blank"
      style={{
        display: "inline-block",
        width: blankWidth || "2.6em",
        borderBottom: `3px dashed ${withAlpha(accent, 0.7)}`,
        height: "0.9em",
        verticalAlign: "baseline",
        margin: "0 4px",
      }}
    />
  );
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: size === "lg" ? "14px 20px" : "10px 16px",
        borderRadius: LEARN.radius.md,
        background: "rgba(255,255,255,0.94)",
        border: `2px solid ${LEARN.color.line}`,
        boxShadow: LEARN.shadow.card,
        fontFamily: LEARN.font,
        fontWeight: 700,
        fontSize: size === "lg" ? 28 : 24,
        lineHeight: 1.35,
        color: LEARN.color.ink,
        flexShrink: 0,
        textAlign: "center",
        ...style,
      }}
    >
      <span style={{ overflowWrap: "anywhere" }}>
        {before}
        {blank}
        {after}
      </span>
      {children}
    </div>
  );
};

/** Two-tone chrome row above the pad: target chip on the left, counters on the right. */
export const RoundBar = ({ left, right, style }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", flexShrink: 0, minWidth: 0, ...style }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flexWrap: "wrap" }}>{left}</div>
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>{right}</div>
  </div>
);
